// ╔══════════════════════════════════════════════════════════════════╗
// ║                   ADMIN DASHBOARD LAYOUT                         ║
// ╚══════════════════════════════════════════════════════════════════╝

import React, { useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import {
  LayoutDashboard,
  Users,
  CreditCard,
  Landmark,
  TrendingUp,
  Bitcoin,
  Gift,
  Building2,
  Settings,
  DollarSign,
  LogOut,
  Menu,
  X,
  ChevronDown,
  Bell,
  Search,
  UserCog,
  FileText,
  HelpCircle,
  Wallet,
  Key,
  Shield
} from 'lucide-react'
import { useAdmin } from './AdminApp'

// Pages
import DashboardHome from './pages/DashboardHome'
import UsersPage from './pages/UsersPage'
import AccountsPage from './pages/AccountsPage'
import LoansPage from './pages/LoansPage'
import InvestmentsPage from './pages/InvestmentsPage'
import CryptoPage from './pages/CryptoPage'
import RewardsPage from './pages/RewardsPage'
import BranchesPage from './pages/BranchesPage'
import AdminUsersPage from './pages/AdminUsersPage'
import SettingsPage from './pages/SettingsPage'
import WalletManagementPage from './pages/WalletManagementPage'
import KycPage from './pages/KycPage'

const menuItems = [
  {
    id: 'dashboard',
    label: 'Dashboard',
    icon: LayoutDashboard,
    permission: 'dashboard.view'
  },
  {
    id: 'users',
    label: 'Customers',
    icon: Users,
    permission: 'users.view'
  },
  {
    id: 'kyc',
    label: 'KYC Verification',
    icon: Shield,
    permission: 'users.kyc',
    badge: 'new'
  },
  {
    id: 'accounts',
    label: 'Accounts',
    icon: CreditCard,
    permission: 'accounts.view'
  },
  {
    id: 'loans',
    label: 'Loans',
    icon: Landmark,
    permission: 'loans.view',
    badge: 'pending'
  },
  {
    id: 'investments',
    label: 'Investments',
    icon: TrendingUp,
    permission: 'investments.view'
  },
  {
    id: 'crypto',
    label: 'Cryptocurrency',
    icon: Bitcoin,
    permission: 'crypto.view'
  },
  {
    id: 'wallets',
    label: 'Wallet Management',
    icon: Wallet,
    permission: 'crypto.manage'
  },
  {
    id: 'rewards',
    label: 'Rewards',
    icon: Gift,
    permission: 'rewards.view'
  },
  {
    id: 'branches',
    label: 'Branches',
    icon: Building2,
    permission: 'branches.view'
  },
  { type: 'divider' },
  {
    id: 'admin-users',
    label: 'Admin Users',
    icon: UserCog,
    permission: 'admin_users.view'
  },
  {
    id: 'settings',
    label: 'Settings',
    icon: Settings,
    permission: 'settings.view'
  }
]

export default function AdminDashboard({ onLogout }) {
  const { admin, hasPermission } = useAdmin()
  const [currentPage, setCurrentPage] = useState('dashboard')
  const [sidebarOpen, setSidebarOpen] = useState(true)
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false)
  const [showProfileMenu, setShowProfileMenu] = useState(false)

  const filteredMenuItems = menuItems.filter(item => {
    if (item.type === 'divider') return true
    return hasPermission(item.permission)
  })

  const renderPage = () => {
    switch (currentPage) {
      case 'dashboard':
        return <DashboardHome />
      case 'users':
        return <UsersPage />
      case 'kyc':
        return <KycPage />
      case 'accounts':
        return <AccountsPage />
      case 'loans':
        return <LoansPage />
      case 'investments':
        return <InvestmentsPage />
      case 'crypto':
        return <CryptoPage />
      case 'wallets':
        return <WalletManagementPage />
      case 'rewards':
        return <RewardsPage />
      case 'branches':
        return <BranchesPage />
      case 'admin-users':
        return <AdminUsersPage />
      case 'settings':
        return <SettingsPage />
      default:
        return <DashboardHome />
    }
  }

  return (
    <div className="min-h-screen bg-slate-900">
      {/* Mobile Sidebar Overlay */}
      <AnimatePresence>
        {mobileSidebarOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 z-40 lg:hidden"
            onClick={() => setMobileSidebarOpen(false)}
          />
        )}
      </AnimatePresence>

      {/* Sidebar */}
      <aside
        className={`fixed top-0 left-0 h-full bg-slate-800 border-r border-slate-700 z-50 transition-all duration-300 ${
          sidebarOpen ? 'w-64' : 'w-20'
        } ${mobileSidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}`}
      >
        {/* Logo */}
        <div className="h-16 flex items-center justify-between px-4 border-b border-slate-700">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-gradient-to-br from-amber-400 via-yellow-500 to-amber-600 rounded-xl flex items-center justify-center shadow-lg shadow-amber-500/20">
              <DollarSign className="w-6 h-6 text-white" />
            </div>
            {sidebarOpen && (
              <div>
                <h1 className="font-bold text-white">Swift Gold</h1>
                <p className="text-xs text-amber-400">Admin Panel</p>
              </div>
            )}
          </div>
          <button
            onClick={() => setMobileSidebarOpen(false)}
            className="lg:hidden p-2 hover:bg-slate-700 rounded-lg"
          >
            <X className="w-5 h-5 text-slate-400" />
          </button>
        </div>

        {/* Menu */}
        <nav className="p-4 space-y-1 overflow-y-auto h-[calc(100vh-8rem)]">
          {filteredMenuItems.map((item, index) => {
            if (item.type === 'divider') {
              return (
                <div key={`div-${index}`} className="my-4 border-t border-slate-700" />
              )
            }

            const Icon = item.icon
            const isActive = currentPage === item.id

            return (
              <button
                key={item.id}
                onClick={() => {
                  setCurrentPage(item.id)
                  setMobileSidebarOpen(false)
                }}
                className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all ${
                  isActive
                    ? 'bg-amber-500/20 text-amber-400'
                    : 'text-slate-400 hover:bg-slate-700/50 hover:text-white'
                }`}
              >
                <Icon className="w-5 h-5 flex-shrink-0" />
                {sidebarOpen && (
                  <>
                    <span className="flex-1 text-left text-sm font-medium">{item.label}</span>
                    {item.badge && (
                      <span className="px-2 py-0.5 bg-amber-500/20 text-amber-400 text-xs font-semibold rounded">
                        {item.badge}
                      </span>
                    )}
                  </>
                )}
              </button>
            )
          })}
        </nav>

        {/* Toggle Sidebar */}
        <div className="absolute bottom-4 left-0 right-0 px-4 hidden lg:block">
          <button
            onClick={() => setSidebarOpen(!sidebarOpen)}
            className="w-full flex items-center justify-center gap-2 px-3 py-2 text-slate-400 hover:text-white hover:bg-slate-700/50 rounded-lg transition-all"
          >
            <ChevronDown className={`w-5 h-5 transition-transform ${sidebarOpen ? 'rotate-90' : '-rotate-90'}`} />
            {sidebarOpen && <span className="text-sm">Collapse</span>}
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <div className={`transition-all duration-300 ${sidebarOpen ? 'lg:ml-64' : 'lg:ml-20'}`}>
        {/* Top Bar */}
        <header className="h-16 bg-slate-800 border-b border-slate-700 flex items-center justify-between px-4 lg:px-6 sticky top-0 z-30">
          <div className="flex items-center gap-4">
            <button
              onClick={() => setMobileSidebarOpen(true)}
              className="lg:hidden p-2 hover:bg-slate-700 rounded-lg"
            >
              <Menu className="w-6 h-6 text-slate-400" />
            </button>

            {/* Search */}
            <div className="hidden md:flex items-center gap-2 px-3 py-2 bg-slate-900/50 border border-slate-700 rounded-lg">
              <Search className="w-4 h-4 text-slate-500" />
              <input
                type="text"
                placeholder="Search..."
                className="bg-transparent border-none outline-none text-slate-300 placeholder-slate-500 w-48 text-sm"
              />
            </div>
          </div>

          <div className="flex items-center gap-3">
            {/* Notifications */}
            <button className="relative p-2 hover:bg-slate-700 rounded-lg transition-colors">
              <Bell className="w-5 h-5 text-slate-400" />
              <span className="absolute top-1 right-1 w-2 h-2 bg-red-500 rounded-full"></span>
            </button>

            {/* Profile */}
            <div className="relative">
              <button
                onClick={() => setShowProfileMenu(!showProfileMenu)}
                className="flex items-center gap-3 px-3 py-2 hover:bg-slate-700 rounded-lg transition-colors"
              >
                <div className="w-8 h-8 bg-gradient-to-br from-amber-400 to-amber-600 rounded-full flex items-center justify-center">
                  <span className="text-white font-semibold text-sm">
                    {admin?.firstName?.[0]}{admin?.lastName?.[0]}
                  </span>
                </div>
                <div className="hidden md:block text-left">
                  <p className="text-sm font-medium text-white">
                    {admin?.firstName} {admin?.lastName}
                  </p>
                  <p className="text-xs text-slate-400">{admin?.role?.displayName}</p>
                </div>
                <ChevronDown className="w-4 h-4 text-slate-400 hidden md:block" />
              </button>

              {/* Dropdown */}
              <AnimatePresence>
                {showProfileMenu && (
                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: 10 }}
                    className="absolute right-0 mt-2 w-56 bg-slate-800 border border-slate-700 rounded-xl shadow-xl overflow-hidden"
                  >
                    <div className="p-3 border-b border-slate-700">
                      <p className="text-sm font-medium text-white">{admin?.email}</p>
                      <p className="text-xs text-slate-400 mt-0.5">{admin?.role?.displayName}</p>
                    </div>
                    <div className="p-2">
                      <button
                        onClick={() => {
                          setCurrentPage('settings')
                          setShowProfileMenu(false)
                        }}
                        className="w-full flex items-center gap-3 px-3 py-2 text-slate-400 hover:bg-slate-700 hover:text-white rounded-lg transition-colors"
                      >
                        <Settings className="w-4 h-4" />
                        <span className="text-sm">Settings</span>
                      </button>
                      <button
                        onClick={() => {
                          setShowProfileMenu(false)
                          onLogout()
                        }}
                        className="w-full flex items-center gap-3 px-3 py-2 text-red-400 hover:bg-red-500/10 rounded-lg transition-colors"
                      >
                        <LogOut className="w-4 h-4" />
                        <span className="text-sm">Sign Out</span>
                      </button>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </div>
        </header>

        {/* Page Content */}
        <main className="p-4 lg:p-6">
          <AnimatePresence mode="wait">
            <motion.div
              key={currentPage}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ duration: 0.2 }}
            >
              {renderPage()}
            </motion.div>
          </AnimatePresence>
        </main>
      </div>
    </div>
  )
}
